import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useWebinarT } from '@/hooks/useWebinarT';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  fetchMyPublicSurveySubmission,
  submitPublicSurvey,
  submitPublicSurveyPut,
} from '@/features/webinar/webinarThunks';
import type { SurveyTemplate } from '@/features/webinar/types';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { toast } from 'react-toastify';

interface WaitingRoomSurveyFormProps {
  waitingRoomSurvey: SurveyTemplate;
  visualIdentity: WorkspaceVisualIdentity;
  theme: 'LIGHT' | 'DARK';
  token: string;
  setIsSurveyActive: (isSurveyActive: boolean) => void;
}

interface SurveyFormData {
  [questionId: string]: string | string[];
}

export default function WaitingRoomSurveyForm({
  waitingRoomSurvey,
  visualIdentity,
  theme,
  token,
  setIsSurveyActive,
}: WaitingRoomSurveyFormProps) {
  const [surveyStep, setSurveyStep] = useState(1);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const hasSubmittedRef = useRef(hasSubmitted);
  const lastSubmittedAnswerRef = useRef<string | string[] | null>(null);
  hasSubmittedRef.current = hasSubmitted;

  const dispatch = useAppDispatch();
  const publicSession = useAppSelector((state) => state.webinar.currentPublicSession);
  const [isSubmittingLocal, setIsSubmittingLocal] = useState(false);
  const submitUiTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { t } = useWebinarT('webinars');

  const clearSubmitUiTimer = () => {
    if (submitUiTimerRef.current != null) {
      clearTimeout(submitUiTimerRef.current);
      submitUiTimerRef.current = null;
    }
  };

  const totalQuestions = waitingRoomSurvey?.questions?.length ?? 0;
  const lastQuestionIndex = totalQuestions > 0 ? totalQuestions - 1 : -1;
  const isLastQuestion = lastQuestionIndex >= 0 && surveyStep - 1 === lastQuestionIndex;

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SurveyFormData>({
    mode: 'onChange',
    defaultValues: {},
  });

  const watchedValues = watch();

  const onFormSubmit = async (data: SurveyFormData) => {
    if (!waitingRoomSurvey?.questions || !token || isSubmittingLocal) return;

    const sessionId = publicSession?.session.id;
    if (!sessionId) {
      console.error('Impossible de soumettre le sondage : sessionId manquant');
      return;
    }

    clearSubmitUiTimer();
    const submitStartedAt = Date.now();
    setIsSubmittingLocal(true);

    try {
      const answersPayload = waitingRoomSurvey.questions.map((item, index) => {
        const questionKey = `question_${index}`;
        const answers = data[questionKey];

        let optionIds: number[] = [];

        if (item.type === 'SINGLE_CHOICE') {
          const answerIndex = parseInt(answers as string);
          const selectedOption = item.options[answerIndex];
          if (selectedOption) {
            optionIds = [selectedOption.id];
          }
        } else {
          const answerIndices = (answers as string[]).map((idx) => parseInt(idx));
          optionIds = answerIndices
            .map((idx) => item.options[idx])
            .filter((opt): opt is (typeof item.options)[number] => !!opt)
            .map((opt) => opt.id);
        }

        return {
          questionId: item.id,
          optionIds,
          freeText: '',
        };
      });

      const payload = {
        webinarId: waitingRoomSurvey.webinarId,
        templateId: waitingRoomSurvey.id,
        data: {
          sessionId,
          answers: answersPayload,
        },
        authorization: token,
      };

      if (hasSubmitted) {
        await dispatch(submitPublicSurveyPut(payload)).unwrap();
        toast.success(t('waitingRoom.survey.saveSuccess'));
      } else {
        await dispatch(submitPublicSurvey(payload)).unwrap();
        setHasSubmitted(true);
        toast.success(t('waitingRoom.survey.saveSuccess'));
      }

      const elapsedMs = Date.now() - submitStartedAt;
      const remainingMs = Math.max(0, 5000 - elapsedMs);
      submitUiTimerRef.current = setTimeout(() => {
        setIsSubmittingLocal(false);
        if (isLastQuestion) {
          setIsSurveyActive(false);
        }
        submitUiTimerRef.current = null;
      }, remainingMs);
    } catch (error) {
      console.error('Erreur lors de la soumission du sondage:', error);
      setIsSubmittingLocal(false);
      if (isLastQuestion) {
        lastSubmittedAnswerRef.current = null;
      }
    }
  };

  useEffect(() => {
    if (!isLastQuestion || lastQuestionIndex < 0) return;

    const lastKey = `question_${lastQuestionIndex}`;
    const value = watchedValues[lastKey];
    const isValid =
      value !== undefined &&
      value !== null &&
      (Array.isArray(value) ? value.length > 0 : value !== '');

    if (!isValid) return;

    const valueKey = JSON.stringify(value);
    if (lastSubmittedAnswerRef.current === valueKey) return;
    lastSubmittedAnswerRef.current = valueKey;

    handleSubmit(onFormSubmit)();
  }, [isLastQuestion, lastQuestionIndex, watchedValues[`question_${lastQuestionIndex}`]]);

  // Quand l'utilisateur quitte l'onglet sondage après avoir soumis, rafraîchir la soumission côté page
  useEffect(() => {
    return () => {
      clearSubmitUiTimer();
      if (
        hasSubmittedRef.current &&
        waitingRoomSurvey?.webinarId != null &&
        waitingRoomSurvey?.id != null &&
        token
      ) {
        dispatch(
          fetchMyPublicSurveySubmission({
            webinarId: waitingRoomSurvey.webinarId,
            templateId: waitingRoomSurvey.id,
            authorization: token,
          }),
        );
      }
    };
  }, [dispatch, waitingRoomSurvey?.webinarId, waitingRoomSurvey?.id, token]);

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="flex flex-col gap-4 w-full">
      <h3
        className="text-xl font-medium"
        style={{
          color: theme === 'DARK' ? 'white' : 'black',
          fontFamily: visualIdentity?.fontFamily || 'Poppins',
        }}
      >
        {waitingRoomSurvey?.title || t('waitingRoom.survey.defaultTitle')}
      </h3>

      <p
        className="text-sm"
        style={{
          fontFamily: visualIdentity?.fontFamily || 'Poppins',
          color: theme === 'DARK' ? 'white' : '#6D6E7A',
        }}
      >
        {waitingRoomSurvey?.subTitle || t('waitingRoom.survey.defaultSubtitle')}
      </p>

      <div className="flex flex-col gap-4">
        {waitingRoomSurvey?.questions?.map((question: any, realIndex: number) => {
          // Afficher seulement la question correspondant à l'étape actuelle
          if (realIndex !== surveyStep - 1) return null;

          return (
            <div key={realIndex}>
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center">
                  {surveyStep > 1 && (
                    <ArrowLeftIcon
                      className={`size-6 text-[#9E9FA6] mr-2 ${
                        isSubmittingLocal ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                      }`}
                      onClick={() => {
                        if (!isSubmittingLocal) setSurveyStep(surveyStep - 1);
                      }}
                    />
                  )}
                  <h3
                    className="text-base font-medium"
                    style={{
                      color: theme === 'DARK' ? 'white' : 'black',
                      fontFamily: visualIdentity?.fontFamily || 'Poppins',
                    }}
                  >
                    <span
                      style={{
                        color: theme === 'DARK' ? '#95B6FF' : '#2C6CFF',
                        fontFamily: visualIdentity?.fontFamily || 'Poppins',
                      }}
                    >
                      {surveyStep}/{waitingRoomSurvey?.questions?.length || 1}
                    </span>{' '}
                    {question.textContent}
                  </h3>
                </div>
                {surveyStep < (waitingRoomSurvey?.questions?.length || 1) && (
                  <ArrowRightIcon
                    className={`size-6 text-[#9E9FA6] ${
                      isSubmittingLocal ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                    }`}
                    onClick={() => {
                      if (!isSubmittingLocal) setSurveyStep(surveyStep + 1);
                    }}
                  />
                )}
              </div>
              <div className="flex flex-col gap-2 pt-4">
                {question.type === 'SINGLE_CHOICE' ? (
                  // Radio buttons pour une seule réponse
                  <Controller
                    name={`question_${realIndex}`}
                    control={control}
                    rules={{
                      required: t('waitingRoom.survey.validation.questionRequired') as string,
                    }}
                    render={({ field }) => (
                      <RadioGroup
                        value={field.value as string}
                        disabled={isSubmittingLocal}
                        onValueChange={(value) => {
                          if (isSubmittingLocal) return;
                          field.onChange(value);

                          const totalQuestions = waitingRoomSurvey?.questions?.length || 1;
                          if (surveyStep < totalQuestions) {
                            setTimeout(() => {
                              setSurveyStep((step) => step + 1);
                            }, 300);
                          }
                        }}
                      >
                        {question.options.map((option: any, optionIndex: number) => (
                          <label
                            key={optionIndex}
                            className={`flex items-center gap-2 p-2 rounded-lg w-full ${
                              isSubmittingLocal ? 'cursor-not-allowed' : 'cursor-pointer'
                            }`}
                            style={{
                              backgroundColor:
                                theme === 'LIGHT'
                                  ? 'rgba(31, 31, 31, 0.03)'
                                  : 'rgba(255, 255, 255, 0.1)',
                            }}
                          >
                            <RadioGroupItem
                              value={optionIndex.toString()}
                              className={`size-6 shrink-0 ${
                                isSubmittingLocal
                                  ? 'cursor-not-allowed opacity-60'
                                  : 'cursor-pointer'
                              }`}
                              disabled={isSubmittingLocal}
                            />
                            <span
                              className="text-base flex-1 text-left"
                              style={{
                                color: theme === 'DARK' ? 'white' : 'black',
                                fontFamily: visualIdentity?.fontFamily || 'Poppins',
                              }}
                            >
                              {option.textContent}
                            </span>
                          </label>
                        ))}
                      </RadioGroup>
                    )}
                  />
                ) : (
                  // Checkboxes pour plusieurs réponses
                  <Controller
                    name={`question_${realIndex}`}
                    control={control}
                    rules={{
                      required: t('waitingRoom.survey.validation.minAnswersRequired') as string,
                    }}
                    render={({ field }) => (
                      <div className="flex flex-col gap-2">
                        {question.options.map((option: any, optionIndex: number) => (
                          <label
                            key={optionIndex}
                            className={`flex items-center gap-2 p-2 rounded-lg w-full ${
                              isSubmittingLocal ? 'cursor-not-allowed' : 'cursor-pointer'
                            }`}
                            style={{
                              backgroundColor:
                                theme === 'LIGHT'
                                  ? 'rgba(31, 31, 31, 0.03)'
                                  : 'rgba(255, 255, 255, 0.1)',
                            }}
                          >
                            <Checkbox
                              className={`size-6 shrink-0 ${
                                isSubmittingLocal
                                  ? 'cursor-not-allowed opacity-60'
                                  : 'cursor-pointer'
                              }`}
                              checked={
                                (field.value as string[])?.includes(optionIndex.toString()) || false
                              }
                              disabled={isSubmittingLocal}
                              onCheckedChange={(checked) => {
                                if (isSubmittingLocal) return;
                                const currentValues = (field.value as string[]) || [];
                                if (checked) {
                                  field.onChange([...currentValues, optionIndex.toString()]);
                                } else {
                                  field.onChange(
                                    currentValues.filter(
                                      (val: string) => val !== optionIndex.toString(),
                                    ),
                                  );
                                }
                              }}
                            />
                            <span
                              className="text-base flex-1 text-left"
                              style={{
                                color: theme === 'DARK' ? 'white' : 'black',
                                fontFamily: visualIdentity?.fontFamily || 'Poppins',
                              }}
                            >
                              {option.textContent}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  />
                )}
                {errors[`question_${realIndex}`] && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors[`question_${realIndex}`]?.message}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pas de bouton Valider sur la dernière question : sauvegarde automatique */}
    </form>
  );
}
